import React, { FC } from 'react';
import { DialogDefault, ThemeTypography, ThemeButton } from 'DesignSystem';
import { Box, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { IPartnerRequest, EPartnerRequestStatus } from 'FeatureModules/Partner/types/partner.types';
import ReceivedRequestCard from 'FeatureModules/Home/Components/Requests/ReceivedRequestCard';
import HtmlField from 'Components/HTMLField';
import useDialog from 'hooks/useDialog';
import { useNavigate } from 'react-router-dom';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { REQUEST_STATUS_CLIENT } from 'FeatureModules/Request/utils/requestStatus.utils';
import usePartnerRequestActions from './usePartnerRequestActions';
import CompanyNameLogo from 'Components/CompanyNameLogo';

const CompanyName = styled(Box)(() => ({
  cursor: 'pointer',
}));

const DismissMessageContainer = styled(Box)(({ theme }) => ({
  background: theme.custom.themeColors.grayScale[20],
  borderRadius: 6,
  padding: theme.spacing(1.2),
  marginTop: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
}));

export interface IRequestDetailsDialogProps extends IDefaultDialogProps {
  request: IPartnerRequest;
}

const RequestDetailsDialog: FC<IRequestDetailsDialogProps> = ({ open, onClose, request }) => {
  const { requestedToCompany, requestReplyMessage, requestedToCompany: partner } = request;
  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const { loading, updatePartnerRequest } = usePartnerRequestActions();

  const resendRequest = () => {
    openDialog({
      type: 'PARTNER_REQUEST',
      props: { requestedToCompany: partner, initialRequest: request },
    });
    onClose?.();
  };

  const handleCloseRequest = async () => {
    await updatePartnerRequest({
      variables: {
        id: request.id,
        input: {
          requestStatus: EPartnerRequestStatus.DELETED,
        },
      },
    });
    onClose?.();
  };

  const navigateToPartner = () => {
    onClose?.();
    navigate(`/partner/${partner.id}`);
  };

  return (
    <DialogDefault
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title="Review response"
      icon={() => (
        <Icon
          name={REQUEST_STATUS_CLIENT[request.requestType][EPartnerRequestStatus.EDITED].iconName}
        />
      )}
      data-testid="request-details-dialog"
    >
      <Box minWidth={550} maxWidth={550} minHeight={200} px={3} py={1}>
        <ReceivedRequestCard request={request} displayYourMessage displayActions={false} />
        <Box mt={4} mb={3}>
          <Divider />
        </Box>
        <Box mt={2} display="flex" alignItems="center">
          <CompanyName onClick={navigateToPartner} display="flex" alignItems="center">
            <CompanyNameLogo companyName={requestedToCompany.name} size={EAvailableSizes.MEDIUM} />
          </CompanyName>
          <Box ml={0.5}>
            <ThemeTypography variant="BODY_LARGE_BOLD">has dismissed your request</ThemeTypography>
          </Box>
        </Box>
        {requestReplyMessage && (
          <DismissMessageContainer>
            <ThemeTypography variant="BODY_LARGE_BOLD">
              {requestedToCompany.name}:&nbsp;
            </ThemeTypography>
            <HtmlField>{requestReplyMessage}</HtmlField>
          </DismissMessageContainer>
        )}
        <Box display="flex" justifyContent="space-between" mt={5} mb={2}>
          <ThemeButton onClick={onClose} color="BLUE_ICE">
            Cancel
          </ThemeButton>
          <Box display="flex" gap={1}>
            <ThemeButton
              color="BLUE_ICE"
              startIcon={<Icon name="delete" size="small" />}
              loading={loading}
              onClick={handleCloseRequest}
            >
              Close request
            </ThemeButton>
            <ThemeButton
              color="PRIMARY"
              startIcon={<Icon name="edit" size="small" color="WHITE" />}
              onClick={resendRequest}
              data-testid="resend-partner-request-btn"
              loading={loading}
            >
              Edit request
            </ThemeButton>
          </Box>
        </Box>
      </Box>
    </DialogDefault>
  );
};

export default RequestDetailsDialog;
